import { BalanceData, GearBonus, GearItem, GearOwner, GearTarget, PlayerProfile, affinityOf, bonusFor, primaryValue, salvageYield, sameOwner } from "@highmarch/domain";
import { LoadoutScope, allTowers, bestLoadout, itemScore, seatsFor } from "./gearPlan";
import { SaveRecord } from "./saves";

/**
 * Read models for the Gear screen, whichever layout shows them: who wears what (`loadoutOf`), what can go
 * (`outclassed`, `salvagePreview`) and what is new since the player last looked (`isNewGear`). Pure over the profile.
 */

/** One of an owner's four sockets. */
export interface SocketView {
  readonly slot: number;
  readonly worn: GearItem | null;
  /** Stash items (not worn anywhere) this owner can wear now that beat `worn` on gear power — any, when it is empty. */
  readonly better: number;
  /** What "Equip best" for the scope puts here, when it changes this socket (null: nothing planned). */
  readonly planned: GearItem | null;
}

/** An owner — the fortress or one tower type — as the loadout overview shows it. */
export interface OwnerView {
  readonly owner: GearOwner;
  /** Index into balance.towers (the fortress at its own index), its id and name. */
  readonly archetype: number;
  readonly id: string;
  readonly name: string;
  readonly isBase: boolean;
  /** Owned now (the fortress always); a locked type names the campaign level that unlocks it. */
  readonly owned: boolean;
  readonly unlockLevel: number | null;
  /** Persistent level, which caps the item level of what it wears. */
  readonly level: number;
  /** The scope's level fields gear here (the fortress always; with no level, every owned type). */
  readonly seat: boolean;
  /** Its best place in the level's per-slot seat lists (0 = first choice); null when it is no seat. */
  readonly seatRank: number | null;
  /** The four sockets of its kind, in slot order. */
  readonly sockets: readonly SocketView[];
  /** Gear power worn (the sum of `itemScore`). */
  readonly power: number;
  /** The stat it answers best to (a GearStat; −1 for the fortress). */
  readonly affinity: number;
  /** The Epic set: worn pieces at the set's rarity or above, of how many, and whether the bonus is on. */
  readonly set: { readonly pieces: number; readonly needed: number; readonly active: boolean };
  /** Its whole gear bonus in a level (stat chips, Mythic effects). */
  readonly bonus: GearBonus;
}

/**
 * Every owner in the order the overview lists them: the fortress, the scope's seats (best rank first), the other
 * owned types, then the locked ones by unlock level. `scope.roster` is the owned types (default: all).
 */
export function loadoutOf(profile: PlayerProfile, scope: LoadoutScope = {}): OwnerView[] {
  const B = profile.balance;
  const roster = scope.roster ?? allTowers(B);
  const seats = seatsFor(profile, scope);
  const plan = bestLoadout(profile, scope);
  const views = B.towers.map((t, i): OwnerView => {
    const owner: GearOwner = t.isBase ? { target: GearTarget.Base, towerIndex: 0 } : { target: GearTarget.Tower, towerIndex: i };
    const owned = t.isBase || roster[i] === true;
    const level = t.isBase ? profile.baseLevel : profile.towerLevels[i];
    const ranks = seats.flatMap((list) => list.map((s, rank) => (sameOwner(s.owner, owner) ? rank : -1)).filter((r) => r >= 0));
    const sockets = B.gear.slots.filter((s) => s.target === owner.target).map((s): SocketView => {
      const worn = profile.equippedIn(owner, s.index);
      const floor = worn === null ? -Infinity : itemScore(B, worn);
      const better = owned ? profile.gear.filter((g) => g.slot === s.index && g.equippedTo === null && g.level <= level && itemScore(B, g) > floor).length : 0;
      const planned = plan.moves.find((m) => m.to !== null && m.item.slot === s.index && sameOwner(m.to, owner))?.item ?? null;
      return { slot: s.index, worn, better, planned };
    });
    const worn = sockets.flatMap((s) => (s.worn === null ? [] : [s.worn]));
    const bonus = bonusFor(B, profile.gear, owner);
    return {
      owner, archetype: i, id: t.id, name: t.name, isBase: t.isBase, owned, unlockLevel: owned ? null : t.unlockLevel, level,
      seat: ranks.length > 0, seatRank: ranks.length > 0 ? Math.min(...ranks) : null, sockets,
      power: worn.reduce((n, g) => n + itemScore(B, g), 0), affinity: affinityOf(B, owner),
      set: { pieces: worn.filter((g) => g.rarity >= B.gear.setMinRarity).length, needed: B.gear.setPieces, active: bonus.setBonus }, bonus,
    };
  });
  const group = (v: OwnerView) => (v.isBase ? 0 : v.seat && v.owned ? 1 : v.owned ? 2 : 3);
  return views.sort((a, b) => group(a) - group(b) || (a.seatRank ?? 0) - (b.seatRank ?? 0) || (a.unlockLevel ?? 0) - (b.unlockLevel ?? 0) || a.archetype - b.archetype);
}

export interface OutclassedOptions {
  /** Tower types that could ever wear a tower item (default: every type — conservative; pass the owned roster to judge by today's towers). */
  readonly roster?: readonly boolean[];
  /** The NEW watermark (`SaveRecord.gearSeenUpTo`): items above it are never junk until seen. */
  readonly seenUpTo?: number;
}

/**
 * Junk: items no owner would ever pick. An item is outclassed when at least as many other items as there are owners
 * for its slot (the fortress: one; a tower slot: each type in the roster) strictly dominate it — same slot, wearable
 * wherever it is (item level ≤ its own), and at least as good in gear power, primary and every affix stat, better
 * in one. Every owner then has something at least as good, and dominance is transitive, so salvaging all of them at
 * once still leaves every owner covered. Never worn, locked, NEW, set rarity (Epic) or above, or Mythic.
 */
export function outclassed(profile: PlayerProfile, opts: OutclassedOptions = {}): GearItem[] {
  const B = profile.balance;
  const roster = opts.roster ?? allTowers(B);
  const towerOwners = B.towers.filter((t, i) => !t.isBase && roster[i]).length;
  const owners = (it: GearItem) => (B.gear.slots[it.slot].target === GearTarget.Base ? 1 : towerOwners);
  return profile.gear.filter((it) => {
    if (it.equippedTo !== null || it.locked || it.rarity >= B.gear.setMinRarity || it.mythicEffect >= 0) return false;
    if (opts.seenUpTo !== undefined && it.id > opts.seenUpTo) return false;
    const need = owners(it);
    return need > 0 && profile.gear.filter((y) => y.id !== it.id && dominates(B, y, it)).length >= need;
  });
}

function affixTotals(it: GearItem): bigint[] {
  const out = [0n, 0n, 0n, 0n, 0n];
  for (const a of it.affixes) out[a.stat] += a.value.raw;
  return out;
}

/** `y` is at least as good as `x` everywhere `x` can go, and better in something. */
function dominates(B: BalanceData, y: GearItem, x: GearItem): boolean {
  if (y.slot !== x.slot || y.level > x.level) return false;
  const power = itemScore(B, y) - itemScore(B, x), primary = primaryValue(B, y).cmp(primaryValue(B, x));
  const ya = affixTotals(y), xa = affixTotals(x);
  if (power < 0 || primary < 0 || ya.some((v, s) => v < xa[s])) return false;
  return power > 0 || primary > 0 || ya.some((v, s) => v > xa[s]);
}

/** What salvaging these items pays (the Clear junk button's gain): gold (`value`) and Forge Essence. */
export function salvagePreview(balance: BalanceData, items: readonly GearItem[]): { readonly count: number; readonly value: number; readonly essence: number } {
  const ys = items.map((it) => salvageYield(balance, it));
  return { count: items.length, value: ys.reduce((a, y) => a + y.gold, 0), essence: ys.reduce((a, y) => a + y.essence, 0) };
}

/** NEW: the item came after the player last opened the Gear screen. False until the watermark exists (`initGearSeen`). */
export function isNewGear(item: GearItem, save: Pick<SaveRecord, "gearSeenUpTo">): boolean {
  return save.gearSeenUpTo !== undefined && item.id > save.gearSeenUpTo;
}

const highestId = (items: readonly GearItem[]) => items.reduce((m, g) => Math.max(m, g.id), 0);

/**
 * Starts the watermark on a save that has none (call after loading, as with `seenTowers`): everything held at that
 * moment counts as seen, so an old save shows nothing as NEW, and a fresh one (no items) shows every drop. True when it
 * changed the save.
 */
export function initGearSeen(save: SaveRecord, items: readonly GearItem[]): boolean {
  if (save.gearSeenUpTo !== undefined) return false;
  save.gearSeenUpTo = highestId(items);
  return true;
}

/**
 * Marks everything held as seen — call when the Gear screen OPENS (after reading `isNewGear` for this render): the
 * hub's SheetGuard removes sheets without their close handlers. Ids only grow, so a watermark is enough. True when it
 * changed the save.
 */
export function markGearSeen(save: SaveRecord, items: readonly GearItem[]): boolean {
  const top = Math.max(save.gearSeenUpTo ?? 0, highestId(items));
  if (top === save.gearSeenUpTo) return false;
  save.gearSeenUpTo = top;
  return true;
}
